/**
 * PR11 (Speed Engine V2), section 11 - frame-delta and catch-up
 * protection. Extracted from `GameScene.update()` into its own pure,
 * framework-independent module for the same reason `completionCoordinator.decideNamePrompt`/
 * `QaMode.shouldPersistRun` already are: `GameScene` is a Phaser `Scene`
 * with no unit test harness in this project, so the actual decision
 * logic lives here instead, directly testable without Phaser at all.
 */

/** A backgrounded/throttled tab can hand back one enormous frame `delta` on resume - clamped before it ever reaches active-time tracking, SPEED TRIAL/Daily pressure timers, or the movement accumulator, so background time is discarded rather than silently counted as real active/trial/pressure time. */
export const MAX_ACTIVE_DELTA_MS = 250;

/** Hard emergency ceiling on movement ticks performed in one render frame - a defensive guard, not a normal code path. At the automatic gate-exit walk's fastest possible interval (40ms), a full `MAX_ACTIVE_DELTA_MS` (250ms) of backlog needs at most 7 ticks to fully drain, comfortably under this. */
export const MAX_MOVEMENT_ITERATIONS_PER_FRAME = 8;

/** Clamps an incoming raw render-frame delta to `MAX_ACTIVE_DELTA_MS` - apply this BEFORE the delta reaches active-time tracking, real-time effect timers, or the movement accumulator. */
export function clampActiveDeltaMs(rawDeltaMs: number): number {
  return Math.min(rawDeltaMs, MAX_ACTIVE_DELTA_MS);
}

export interface MovementCatchUpInput {
  /** The accumulator's value going into this frame (before adding `deltaMs`). */
  accumulatorMs: number;
  /** This frame's delta - already clamped via `clampActiveDeltaMs()` by the caller. */
  deltaMs: number;
  /** Reads the CURRENT step interval - called fresh before each prospective tick, since a tick can itself change it (Growth/RUSH debt/SPEED TRIAL/the exit walk). */
  getStepMs: () => number;
  /** Performs exactly one grid tick - the caller's own `tick()`. May itself end the run mid-loop (game-over/level-complete). */
  tick: () => void;
  /** True once the run has ended - stops the loop immediately, same as the accumulator loop's own early-exit condition before PR11. */
  hasEnded: () => boolean;
  maxIterations?: number;
}

/**
 * Runs as many movement ticks as `accumulatorMs + deltaMs` calls for
 * (re-reading `getStepMs()` before each one, since Growth/RUSH debt/
 * SPEED TRIAL/the exit walk can all change it mid-loop), capped at
 * `maxIterations` (default `MAX_MOVEMENT_ITERATIONS_PER_FRAME`). Returns
 * the new accumulator value. If the iteration cap is ever actually
 * reached, the remaining backlog is discarded outright (returns 0)
 * rather than carried into the next frame, so a burst can never cascade
 * into a second one.
 */
export function runMovementCatchUp(input: MovementCatchUpInput): number {
  let accumulatorMs = input.accumulatorMs + input.deltaMs;
  const maxIterations = input.maxIterations ?? MAX_MOVEMENT_ITERATIONS_PER_FRAME;
  let iterations = 0;
  for (;;) {
    const stepMs = input.getStepMs();
    if (accumulatorMs < stepMs || input.hasEnded()) break;
    if (iterations >= maxIterations) {
      accumulatorMs = 0;
      break;
    }
    accumulatorMs -= stepMs;
    input.tick();
    iterations += 1;
  }
  return accumulatorMs;
}
